import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import NavigationBar from '../NavigationBar';
import Footer from '../Footer';
import keyService from '../../services/keyService';
import LocalStorageService from '../../services/localStorageService';
import { useTranslation } from '../../i18n';
import './NewTextbox.css';

// Characters of ciphertext per QR code (must match NewTextboxResult.CHARACTERLIMITS)
const QR_CHARACTER_LIMIT = 156;

// Ready-made templates for the situations people actually secure on paper.
// Each template is also deep-linkable: /create_encrypted_text?template=<id>
export const TEMPLATES = [
  {
    id: 'seed-phrase',
    icon: 'fa-coins',
    name: 'Wallet seed phrase',
    subject: 'Crypto wallet seed phrase',
    content: 'Wallet name: \nWallet type (hardware/software): \n\nSeed phrase (words in order):\n1. \n2. \n3. \n4. \n5. \n6. \n7. \n8. \n9. \n10. \n11. \n12. \n\nPassphrase (25th word), if any: ',
  },
  {
    id: '2fa-codes',
    icon: 'fa-mobile',
    name: '2FA backup codes',
    subject: '2FA backup codes',
    content: 'Service: \nAccount / email: \n\nBackup codes:\n- \n- \n- \n- \n- \n',
  },
  {
    id: 'emergency-access',
    icon: 'fa-heartbeat',
    name: 'Emergency access',
    subject: 'Emergency access for my family',
    content: 'If something happens to me, this gives you access to what matters.\n\nPassword manager: \nMaster password: \nPhone PIN: \nEmail account + password: \nBank access: \n\nPeople to contact:\n- \n\nNotes: ',
  },
  {
    id: 'bank-details',
    icon: 'fa-university',
    name: 'Banking details',
    subject: 'Banking details',
    content: 'Bank: \nLogin: \nPassword: \nCard PIN: \nAccount number: \n\nNotes: ',
  },
  {
    id: 'wifi',
    icon: 'fa-wifi',
    name: 'Wi-Fi & home codes',
    subject: 'Home network and codes',
    content: 'Wi-Fi network: \nWi-Fi password: \nRouter admin password: \nAlarm code: \nSafe combination: \n\nNotes: ',
  },
];

// Translated copies of the templates for the active language.
// Template ids and icons stay identical (they are used in ?template= URLs).
export const getTemplates = (t) =>
  TEMPLATES.map((template) => ({
    ...template,
    name: t(`templates.${template.id}.name`),
    subject: t(`templates.${template.id}.subject`),
    content: t(`templates.${template.id}.content`),
  }));

// Estimate how many QR codes the encrypted output will need
export const estimateQrCount = (text, usePassphrase) => {
  if (!text) return 0;
  const utf8Length = new TextEncoder().encode(text).length;
  const payloadBytes = (usePassphrase ? 16 : 0) + 12 + utf8Length + 16; // salt + iv + ct + tag
  const base64Length = 4 * Math.ceil(payloadBytes / 3);
  const prefixLength = usePassphrase ? '2p|600000|'.length : '2|'.length;
  return Math.max(1, Math.ceil((base64Length + prefixLength) / QR_CHARACTER_LIMIT));
};

function NewTextbox({
  text,
  handleTextChange,
  handleSubjectChange,
  applyTemplate,
  encryptedText,
  encryptionKey,
  isLoggedIn,
  subject,
  createdDate,
  onGenerateEncryption
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const templates = getTemplates(t);
  const [useExistingKey, setUseExistingKey] = useState(false);
  const [existingKeys, setExistingKeys] = useState([]);
  const [localKeys, setLocalKeys] = useState([]);
  const [allAvailableKeys, setAllAvailableKeys] = useState([]);
  const [selectedKeyId, setSelectedKeyId] = useState('');
  const [isLoadingKeys, setIsLoadingKeys] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  // Protection method: random key (default) or a passphrase the user remembers
  const [usePassphrase, setUsePassphrase] = useState(false);
  const [passphrase, setPassphrase] = useState('');
  const [passphraseConfirm, setPassphraseConfirm] = useState('');
  const [passphraseError, setPassphraseError] = useState(null);

  // Fetch existing keys when component mounts
  useEffect(() => {
    fetchAllAvailableKeys();
  }, [isLoggedIn]);

  // Apply a template from the URL (?template=seed-phrase) on first render
  useEffect(() => {
    const templateId = searchParams.get('template');
    if (templateId) {
      const template = templates.find(tpl => tpl.id === templateId);
      if (template) {
        setSelectedTemplateId(template.id);
        applyTemplate(template.subject, template.content);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select first key when switching to "Use Existing Key"
  useEffect(() => {
    if (useExistingKey && allAvailableKeys.length > 0 && !selectedKeyId) {
      const firstKey = allAvailableKeys[0];
      setSelectedKeyId(firstKey.id);
    }
  }, [useExistingKey, allAvailableKeys, selectedKeyId]);

  const fetchAllAvailableKeys = async () => {
    try {
      setIsLoadingKeys(true);
      let serverKeys = [];
      let localStorageKeys = [];

      if (isLoggedIn) {
        try {
          serverKeys = await keyService.getKeys();
          setExistingKeys(serverKeys);
        } catch (error) {
          console.error("Error fetching server keys:", error);
        }
      }

      try {
        localStorageKeys = LocalStorageService.getKeys();
        setLocalKeys(localStorageKeys);
      } catch (error) {
        console.error("Error fetching local keys:", error);
      }

      const combinedKeys = [...serverKeys, ...localStorageKeys];
      setAllAvailableKeys(combinedKeys);

      setIsLoadingKeys(false);
    } catch (error) {
      console.error("Error fetching encryption keys:", error);
      setIsLoadingKeys(false);
    }
  };

  const handleTemplateClick = (template) => {
    if (selectedTemplateId === template.id) {
      setSelectedTemplateId(null);
      return;
    }
    setSelectedTemplateId(template.id);
    applyTemplate(template.subject, template.content);
  };

  const validatePassphrase = () => {
    if (passphrase.length < 8) {
      return t('newTextbox.passphraseTooShort');
    }
    if (passphrase !== passphraseConfirm) {
      return t('newTextbox.passphraseMismatch');
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (usePassphrase) {
      const validationError = validatePassphrase();
      setPassphraseError(validationError);
      if (validationError) {
        return;
      }
    }

    setIsSubmitting(true);
    setCurrentStep(2);

    let result = null;
    let selectedKey = null;

    try {
      if (usePassphrase) {
        result = await onGenerateEncryption(null, passphrase);
      } else if (useExistingKey && selectedKeyId) {
        selectedKey = allAvailableKeys.find(key => key.id === selectedKeyId);
        if (selectedKey) {
          result = await onGenerateEncryption(selectedKey);
        }
      } else {
        result = await onGenerateEncryption();
      }

      const finalEncryptionKey = usePassphrase ? null : (
        result?.key?.encryption_key ||
        result?.key?.encryptionKey ||
        selectedKey?.encryption_key ||
        selectedKey?.encryptionKey ||
        encryptionKey
      );

      navigate('/encrypted_text_result', {
        state: {
          encryptedText: result?.encryptedText || encryptedText,
          encryptionKey: finalEncryptionKey,
          subject: result?.subject || subject,
          messageId: result?.message?.id,
          keyId: result?.key?.id,
          useExistingKey: !usePassphrase && useExistingKey && !!selectedKeyId,
          isPassphrase: usePassphrase,
          isLoggedIn
        }
      });
    } catch (error) {
      console.error("Error during encryption:", error);
      setCurrentStep(1);
    } finally {
      setIsSubmitting(false);
    }
  };

  const qrEstimate = estimateQrCount(text, usePassphrase);

  // Pick the right plural dictionary key for the QR estimate (en: one/other, pl: one/few/many)
  const qrCountKey = (count) => {
    if (count === 1) return 'newTextbox.qrCodesOne';
    const few = count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14);
    return few ? 'newTextbox.qrCodesFew' : 'newTextbox.qrCodesMany';
  };

  return (
    [
      <Helmet key="helmet">
        <title>Create Encrypted Text - SecurePaper App</title>
        <meta name="description" content="Create encrypted SecurePaper codes for your sensitive information. Encrypt passwords, seed phrases, PINs, and confidential data with AES-256 encryption." />
        <meta name="keywords" content="encrypt text, create encrypted document, password encryption, seed phrase backup, 2FA backup codes, secure text encryption, SecurePaper code generator" />
        <meta property="og:title" content="Create Encrypted Text - SecurePaper App" />
        <meta property="og:description" content="Create encrypted SecurePaper codes for your sensitive information with AES-256 encryption." />
        <meta property="og:url" content="https://app.securepaper.app/create_encrypted_text" />
        <link rel="canonical" href="https://app.securepaper.app/create_encrypted_text" />
      </Helmet>,
      <NavigationBar key="navbar" />,
      <div className="container encryption-container" key="container">
        <div className="row">
          <Link className="back-link" to="/"><i className="fa fa-arrow-left" aria-hidden="true"></i> {t('common.backToHome')}</Link>
        </div>

        <div className="encryption-header">
          <h1 className="text-center">{t('newTextbox.title')}</h1>
          <p className="text-center text-muted">{t('newTextbox.subtitle')}</p>
        </div>

        <div className="row justify-content-center">
          <div className="col-12 col-md-10 col-lg-8">
            <div className="encryption-card">
              <div className="encryption-progress">
                <div className={`progress-step ${currentStep === 1 ? 'active' : ''}`}>
                  <div className="step-number">1</div>
                  <div className="step-label">{t('newTextbox.step1')}</div>
                </div>
                <div className="progress-line"></div>
                <div className={`progress-step ${currentStep === 2 ? 'active' : ''}`}>
                  <div className="step-number">2</div>
                  <div className="step-label">{t('newTextbox.step2')}</div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="encryption-form">
                <div className="form-group">
                  <label><i className="fa fa-magic form-icon" aria-hidden="true"></i> {t('newTextbox.startFromTemplate')} <span className="text-muted">{t('newTextbox.optional')}</span></label>
                  <div className="template-chips">
                    {templates.map(template => (
                      <button
                        type="button"
                        key={template.id}
                        className={`template-chip ${selectedTemplateId === template.id ? 'active' : ''}`}
                        onClick={() => handleTemplateClick(template)}
                      >
                        <i className={`fa ${template.icon}`} aria-hidden="true"></i> {template.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject"><i className="fa fa-tag form-icon" aria-hidden="true"></i> {t('newTextbox.paperName')}</label>
                  <input
                    type="text"
                    maxLength="50"
                    className="form-control"
                    id="subject"
                    onChange={handleSubjectChange}
                    value={subject}
                    placeholder={t('newTextbox.paperNamePlaceholder')}
                  />
                </div>

                <div className="form-group encryption-key-options">
                  <span className="group-label"><i className="fa fa-shield-halved form-icon" aria-hidden="true"></i> {t('newTextbox.protectionMethod')}</span>

                  <div className="key-option-cards">
                    <label className={`key-option-card ${!usePassphrase ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="protectionMethod"
                        checked={!usePassphrase}
                        onChange={() => setUsePassphrase(false)}
                        className="key-radio"
                      />
                      <div className="key-option-content">
                        <i className="fa fa-key key-icon" aria-hidden="true"></i>
                        <span className="key-option-title">{t('newTextbox.encryptionKeyOption')}</span>
                        <p>{t('newTextbox.encryptionKeyOptionDesc', { location: t(isLoggedIn ? 'newTextbox.savedToAccount' : 'newTextbox.savedInBrowser') })}</p>
                      </div>
                    </label>

                    <label className={`key-option-card ${usePassphrase ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="protectionMethod"
                        checked={usePassphrase}
                        onChange={() => setUsePassphrase(true)}
                        className="key-radio"
                      />
                      <div className="key-option-content">
                        <i className="fa fa-user-secret key-icon" aria-hidden="true"></i>
                        <span className="key-option-title">{t('newTextbox.passphraseOption')}</span>
                        <p>{t('newTextbox.passphraseOptionDesc')}</p>
                      </div>
                    </label>
                  </div>

                  {usePassphrase && (
                    <div className="passphrase-fields fade-in">
                      <div className="form-group">
                        <label htmlFor="passphrase">{t('newTextbox.passphraseLabel')}</label>
                        <input
                          type="password"
                          id="passphrase"
                          className="form-control"
                          value={passphrase}
                          onChange={(e) => { setPassphrase(e.target.value); setPassphraseError(null); }}
                          placeholder={t('newTextbox.passphrasePlaceholder')}
                          autoComplete="new-password"
                          required={usePassphrase}
                        />
                      </div>
                      <div className="form-group">
                        <label htmlFor="passphraseConfirm">{t('newTextbox.repeatPassphrase')}</label>
                        <input
                          type="password"
                          id="passphraseConfirm"
                          className="form-control"
                          value={passphraseConfirm}
                          onChange={(e) => { setPassphraseConfirm(e.target.value); setPassphraseError(null); }}
                          placeholder={t('newTextbox.repeatPassphrasePlaceholder')}
                          autoComplete="new-password"
                          required={usePassphrase}
                        />
                      </div>
                      {passphraseError && (
                        <div className="alert alert-danger py-2">{passphraseError}</div>
                      )}
                      <div className="alert alert-warning py-2">
                        <i className="fa fa-exclamation-triangle" aria-hidden="true"></i> {t('newTextbox.passphraseWarning')}
                      </div>
                    </div>
                  )}
                </div>

                {!usePassphrase && allAvailableKeys.length > 0 && (
                  <div className="form-group encryption-key-options">
                    <span className="group-label"><i className="fa fa-key form-icon" aria-hidden="true"></i> {t('newTextbox.encryptionKeyLabel')}</span>

                    <div className="key-option-cards">
                      <label className={`key-option-card ${!useExistingKey ? 'active' : ''}`}>
                        <input
                          type="radio"
                          id="newKeyRadio"
                          name="keyOption"
                          checked={!useExistingKey}
                          onChange={() => {
                            setUseExistingKey(false);
                            setSelectedKeyId('');
                          }}
                          className="key-radio"
                        />
                        <div className="key-option-content">
                          <i className="fa fa-plus-circle key-icon" aria-hidden="true"></i>
                          <span className="key-option-title">{t('newTextbox.createNewKey')}</span>
                          <p>{t('newTextbox.createNewKeyDesc')}</p>
                        </div>
                      </label>

                      <label className={`key-option-card ${useExistingKey ? 'active' : ''}`}>
                        <input
                          type="radio"
                          id="existingKeyRadio"
                          name="keyOption"
                          checked={useExistingKey}
                          onChange={() => setUseExistingKey(true)}
                          className="key-radio"
                        />
                        <div className="key-option-content">
                          <i className="fa fa-unlock-alt key-icon" aria-hidden="true"></i>
                          <span className="key-option-title">{t('newTextbox.useExistingKey')}</span>
                          <p>
                            {t('newTextbox.chooseFromKeys', { count: allAvailableKeys.length })}
                            {!isLoggedIn && localKeys.length > 0 && ` ${t('newTextbox.localSuffix', { count: localKeys.length })}`}
                            {isLoggedIn && existingKeys.length > 0 && localKeys.length > 0 && ` ${t('newTextbox.accountLocalSuffix', { account: existingKeys.length, local: localKeys.length })}`}
                          </p>
                        </div>
                      </label>
                    </div>

                    {useExistingKey && (
                      <div className="key-selector fade-in">
                        <select
                          className="form-control"
                          value={selectedKeyId}
                          onChange={(e) => setSelectedKeyId(e.target.value)}
                          disabled={isLoadingKeys}
                          required={useExistingKey}
                          style={{ height: 'calc(2.75rem + 2px)', width: '100%', textOverflow: 'ellipsis', whiteSpace: 'normal' }}
                        >
                          <option value="">{t('newTextbox.selectKey')}</option>
                          {existingKeys.length > 0 && (
                            <optgroup label={t('newTextbox.accountKeys')}>
                              {existingKeys.map(key => (
                                <option
                                  key={key.id}
                                  value={key.id}
                                  title={key.name || t('newTextbox.keyCreatedOn', { date: new Date(key.created_at).toLocaleDateString() })}
                                >
                                  {key.name || t('newTextbox.keyCreatedOn', { date: new Date(key.created_at).toLocaleDateString() })}
                                </option>
                              ))}
                            </optgroup>
                          )}
                          {localKeys.length > 0 && (
                            <optgroup label={t('newTextbox.localKeys')}>
                              {localKeys.map(key => (
                                <option
                                  key={key.id}
                                  value={key.id}
                                  title={key.name || t('newTextbox.localKeyCreatedOn', { date: new Date(key.created_at).toLocaleDateString() })}
                                >
                                  {key.name || t('newTextbox.localKeyCreatedOn', { date: new Date(key.created_at).toLocaleDateString() })} {t('newTextbox.localTag')}
                                </option>
                              ))}
                            </optgroup>
                          )}
                        </select>
                        {isLoadingKeys && <div className="loading-indicator"><i className="fa fa-spinner fa-spin" aria-hidden="true"></i> {t('newTextbox.loadingKeys')}</div>}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="textBody"><i className="fas fa-file-alt form-icon" aria-hidden="true"></i> {t('newTextbox.contentToSecure')}</label>
                  <textarea
                    required
                    className="form-control"
                    id="textBody"
                    rows="6"
                    onChange={handleTextChange}
                    placeholder={t('newTextbox.contentPlaceholder')}
                    value={text}
                  />
                  <div className="textarea-footer">
                    <small className="text-muted">{t('newTextbox.contentHint')}</small>
                    <small className="char-count">
                      {t('newTextbox.charCount', { count: text ? text.length : 0 })}
                      {text && (
                        <> &middot; {t(qrCountKey(qrEstimate), { count: qrEstimate })}</>
                      )}
                    </small>
                  </div>
                  {qrEstimate > 8 && (
                    <div className="alert alert-info py-2 mt-2">
                      <i className="fa fa-info-circle" aria-hidden="true"></i> {t('newTextbox.longContentWarning', { count: qrEstimate })}
                    </div>
                  )}
                </div>

                <div className="form-action">
                  <button
                    type="submit"
                    className="btn btn-primary btn-lg generate-btn"
                    disabled={isSubmitting || (!usePassphrase && useExistingKey && !selectedKeyId) || !text}
                  >
                    {isSubmitting ? (
                      <><i className="fa fa-spinner fa-spin" aria-hidden="true"></i> {t('newTextbox.generating')}</>
                    ) : (
                      <><i className="fa fa-lock" aria-hidden="true"></i> {t('newTextbox.generate')}</>
                    )}
                  </button>

                </div>
              </form>

              <div className="encryption-info">
                <div className="info-item">
                  <i className="fas fa-shield-alt info-icon" aria-hidden="true"></i>
                  <div className="info-content">
                    <h5>{t('newTextbox.info1Title')}</h5>
                    <p>{t('newTextbox.info1Desc')}</p>
                  </div>
                </div>
                <div className="info-item">
                  <i className="fa fa-print info-icon" aria-hidden="true"></i>
                  <div className="info-content">
                    <h5>{t('newTextbox.info2Title')}</h5>
                    <p>{t('newTextbox.info2Desc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>,
      <Footer key="footer" />,
    ]
  );
}

export default NewTextbox;
